import React, { useEffect, useState } from "react";
import { db } from "../firebase/firebase-config";
import {
    collection,
    query,
    where,
    getDocs,
    doc,
    getDoc,
} from "firebase/firestore";
import { useAuth } from "../hooks/useAuth";
import { Link, useNavigate } from "react-router-dom";
import QRCode from "qrcode.react"; // npm install qrcode.react
import { auth } from "../firebase/firebase-config";
import { signOut } from "firebase/auth";
import DogLocationMap from "./DogLocationMap";
import "../styles/MembersPage.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSignOutAlt,
    faPlus,
    faCircleXmark,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "react-modal";

// Ensure you set the app element for accessibility reasons
Modal.setAppElement("#root");

const MembersPage = () => {
    const [dogs, setDogs] = useState([]);
    const [selectedDog, setSelectedDog] = useState(null);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (user) {
            const fetchDogs = async () => {
                const q = query(
                    collection(db, "dogs"),
                    where("ownerId", "==", user.uid)
                );
                const querySnapshot = await getDocs(q);
                const dogList = querySnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setDogs(dogList);
            };

            fetchDogs();
        }
    }, [user]);

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate("/login"); // Redirect to login page or wherever you wish
        } catch (error) {
            alert("Failed to log out: " + error.message);
        }
    };

    const handleImageClick = (dog) => {
        setSelectedDog(dog);
    };

    const closeModal = () => {
        setSelectedDog(null);
    };

    const handleRefresh = async (dog) => {
        try {
            const docRef = doc(db, "dogs", dog.id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setSelectedDog({ id: docSnap.id, ...docSnap.data() });
            } else {
                console.error("No such document!");
            }
        } catch (error) {
            console.error("Failed to refresh dog data: ", error);
        }
    };

    return (
        <div className="member-dashboard">
            <h1>My Pets</h1>
            <div className="menuBtns">
                <button onClick={handleLogout} className="newPet">
                    <FontAwesomeIcon icon={faSignOutAlt} />
                </button>
                <Link to="/add-dog" className="newPet">
                    <FontAwesomeIcon icon={faPlus} />
                </Link>
            </div>
            {dogs.length > 0 ? (
                <ul>
                    {dogs.map((dog) => (
                        <li key={dog.id}>
                            <h2 className="dogsName">{dog.name}</h2>
                            <img
                                src={dog.photoUrl}
                                alt={dog.name}
                                className="dogImage"
                                onClick={() => handleImageClick(dog)}
                            />
                        </li>
                    ))}
                    <div className="Spacer"></div>
                </ul>
            ) : (
                <p>No pets added yet.</p>
            )}
            <Modal
                isOpen={!!selectedDog}
                onRequestClose={closeModal}
                contentLabel="Dog Information"
                className="dog-modal"
                overlayClassName="dog-modal-overlay"
            >
                {selectedDog && (
                    <div className="dog-details">
                        <button onClick={closeModal} className="close-modal">
                            <FontAwesomeIcon icon={faCircleXmark} size="2x" />
                        </button>
                        {selectedDog.photoUrl && (
                            <img
                                src={selectedDog.photoUrl}
                                alt="Dog"
                                className="ModalImage"
                            />
                        )}
                        <div className="info">
                            <h2>{selectedDog.name}</h2>
                            <Link
                                to={`/edit-dog/${selectedDog.id}`}
                                className="newPet"
                            >
                                Edit {selectedDog.name}'s Information
                            </Link>
                            <h4>{selectedDog.breed}</h4>
                            <h5>{selectedDog.description}</h5>
                            <div className="mapContainer">
                                {selectedDog.lastKnownLocation && (
                                    <DogLocationMap
                                        location={selectedDog.lastKnownLocation}
                                        zoom={16}
                                    />
                                )}
                            </div>
                            <div
                                className="refresh"
                                onClick={() => handleRefresh(selectedDog)}
                            >
                                <h3>Refresh Information</h3>
                            </div>
                            <div className="allInfo">
                                <div className="dogsInfo">
                                    <p>
                                        <strong>{selectedDog.age}</strong>
                                        <br />
                                        Age
                                    </p>
                                    <p>
                                        <strong>{selectedDog.gender}</strong>
                                        <br />
                                        Gender
                                    </p>
                                </div>
                                <div className="OwnersContactInfo">
                                    <p>
                                        <strong>
                                            {selectedDog.phoneNumber}
                                        </strong>
                                        <br />
                                        Primary
                                    </p>
                                    <p>
                                        <strong>
                                            {selectedDog.phoneNumber2}
                                        </strong>
                                        <br />
                                        Secondary
                                    </p>
                                    <p>
                                        <strong>{selectedDog.email}</strong>
                                        <br />
                                        Email
                                    </p>
                                    <p>
                                        <strong>{selectedDog.address}</strong>
                                        <br />
                                        Address
                                    </p>
                                </div>
                                <div className="optionalDescriptions">
                                    <p>
                                        <strong>
                                            {!selectedDog.isFriendlyWithDogs
                                                ? "Is Not Friendly With Dogs"
                                                : "Is Friendly With Dogs"}
                                        </strong>
                                    </p>
                                    <p>
                                        <strong>
                                            {!selectedDog.isFriendlyWithKids
                                                ? "Is Not Friendly With Kids"
                                                : "Is Friendly With Kids"}
                                        </strong>
                                    </p>
                                    <p>
                                        <strong>
                                            {selectedDog.microchipped
                                                ? "Is Microchipped"
                                                : "Is Not Microchipped"}
                                        </strong>
                                    </p>
                                    <p>
                                        <strong>
                                            {selectedDog.rabiesShot
                                                ? "Has Rabies Shot"
                                                : "Does Not Have Rabies Shot"}
                                        </strong>
                                    </p>
                                    <p>
                                        <strong>
                                            {selectedDog.spayedNeutered
                                                ? "Is Spayed/Neutered"
                                                : "Is Not Spayed/Neutered"}
                                        </strong>
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="qr-code">
                            <QRCode value={selectedDog.qrCodeUrl} />
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default MembersPage;
